import { WithStatePaginatedTable } from '@delmonicos/shared-design-system-react/src/components/with-state-paginated-table/with-state-paginated-table';
import { GatewayTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/outputs/gateway-token.model';
import { TransformedPaginatedData } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transformed-paginated-data.model';
import {
  ColumnOrderState,
  OnChangeFn,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StateEnum } from '@model/enum/state.enum';

import { TokenListActionsColumn } from './token-list-actions.column';
import {
  TOKEN_LIST_COLUMN_LIST,
  TOKEN_LIST_COLUMN_ORDER,
  TOKEN_LIST_COLUMN_VISIBILITY,
  tokenListColumnHelper,
} from './token-list.config';

interface TokenListViewProps {
  paginationLimit: number;
  paginationLimitList: number[];
  paginationState: StateEnum;
  paginationSorting: SortingState;
  resource?: TransformedPaginatedData<GatewayTokenModel>;
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  onPaginationLimitChange: (paginationLimitNext: number) => void;
  onPaginationPageChange: (paginationPageNext: number) => void;
  onPaginationSortingChange?: OnChangeFn<SortingState>;
  renderColumnSettings?: (close: () => void) => React.ReactNode;
  onHideColumn?: (columnId: string) => void;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
}

export const TokenListView = ({
  paginationLimit,
  paginationLimitList,
  paginationState,
  paginationSorting,
  resource,
  columnVisibility = TOKEN_LIST_COLUMN_VISIBILITY,
  columnOrder = TOKEN_LIST_COLUMN_ORDER,
  onPaginationLimitChange,
  onPaginationPageChange,
  onPaginationSortingChange,
  renderColumnSettings,
  onHideColumn,
  onColumnOrderChange,
}: TokenListViewProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // LIST //////////////////////////////////////////////////////////////////////

  const columns = useMemo(
    () => [
      ...TOKEN_LIST_COLUMN_LIST,
      tokenListColumnHelper.display(TokenListActionsColumn),
    ],
    [],
  );

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <WithStatePaginatedTable<GatewayTokenModel>
      paginatedData={resource}
      state={paginationState}
      sorting={paginationSorting}
      columns={columns}
      columnOrder={columnOrder}
      columnVisibility={columnVisibility}
      limit={paginationLimit}
      limitList={paginationLimitList}
      errorMessage={t('$.token-list.error')}
      onLimitChange={onPaginationLimitChange}
      onPageChange={onPaginationPageChange}
      onSortingChange={onPaginationSortingChange}
      renderColumnSettings={renderColumnSettings}
      onHideColumn={onHideColumn}
      onColumnOrderChange={onColumnOrderChange}
    />
  );
};
